/**
 * CLN — "Candle + Linear regressioN".
 *
 * A composite candle type that draws TWO synchronised layers on the same price
 * chart:
 *
 *   1. the REAL market candle (true OHLC, wicks governed by the Wick toggle);
 *   2. a linear-regression BODY drawn on top of it, always wickless.
 *
 * The semantic invariant is that the LINEAR REGRESSION direction decides the
 * colour of BOTH layers. A red (bearish) real candle sitting inside a rising
 * regression prints in the bullish hue — that disagreement is the whole point
 * of the candle type, so the real candle's own open/close direction must never
 * be allowed to recolour it.
 *
 * WHY THIS IS NOT A CandleRow[] -> CandleRow[] TRANSFORM
 * Every other candle type (HA / CSH / CSL / CLH) replaces the bar geometry, so
 * it fits `transformCandles`. CLN needs two geometries at once, which that
 * contract cannot express. So `transformCandles(x, 'CLN')` is a PASSTHROUGH —
 * the chart series keeps the real OHLC, which keeps the crosshair, the OHLC
 * legend, the price scale, the LTP line and the P&L badge all reading true
 * prices — and the regression bodies are painted over it by LrBodyPrimitive.
 *
 * This module is pure: no React, no rendering, no lightweight-charts types.
 */
import { CandleRow } from '../components/charts/TradingChart';
import { linreg } from './candleTransforms';

/** Default regression length. Matches the LR_LEN already used by CSL/CLH so a
 *  user switching between the regression-based candle types sees a comparable
 *  amount of smoothing. Configurable via settings.candle.clnLength. */
export const CLN_DEFAULT_LENGTH = 14;

/** Lookback for the relative-volume baseline used by the contradiction accent. */
const VOL_LOOKBACK = 20;

/** How far relative volume must sit BELOW its baseline before a bar counts as
 *  "unconfirmed" (a directional regression body printed on thin participation).
 *  Expressed in standard deviations of the rolling window. */
const CONTRADICTION_Z = -0.8;

export interface ClnBar {
  time: number;
  /** Regression value of the bar's OPEN. */
  lrOpen: number;
  /** Regression value of the bar's CLOSE. */
  lrClose: number;
  /** Body extremes. There is deliberately no lrHigh/lrLow: regressing the real
   *  high and low independently produces wicks that mean nothing, so the LR
   *  layer is body-only in every state. */
  lrBodyHigh: number;
  lrBodyLow: number;
  /** LR direction — the single source of truth for this bar's colour. */
  up: boolean;
  /** Rolling z-score of this bar's volume against the last VOL_LOOKBACK bars.
   *  null when the feed carries no usable volume (some synthetic series). */
  volZ: number | null;
  /** True when the regression body is directional but participation is thin —
   *  surfaced only as an optional border accent, never as a hue or a geometry
   *  change. */
  volContradiction: boolean;
}

/**
 * Compute the regression bodies (and the volume-participation flags) for a bar
 * series. Reuses the exported `linreg` from candleTransforms rather than
 * reimplementing the fit: that version already fits over `n = min(period, i+1)`
 * during warm-up instead of passing raw price through, which is what stops the
 * body from jumping when a chain re-seeds on a window change.
 *
 * `length` is clamped to a sane range; callers may pass a user setting straight in.
 */
export function computeClnBars(candles: CandleRow[], length = CLN_DEFAULT_LENGTH): ClnBar[] {
  if (!candles.length) return [];
  const n = Math.max(2, Math.min(200, Math.round(length) || CLN_DEFAULT_LENGTH));

  const lrO = linreg(candles.map(c => c.open), n);
  const lrC = linreg(candles.map(c => c.close), n);

  // Rolling volume z-score. Bars before the window is full, and feeds with no
  // volume at all, report null rather than a fabricated score.
  const vols = candles.map(c => (typeof c.volume === 'number' && isFinite(c.volume) ? c.volume : NaN));
  const hasVolume = vols.some(v => isFinite(v) && v > 0);

  const out: ClnBar[] = new Array(candles.length);
  for (let i = 0; i < candles.length; i++) {
    const o = lrO[i];
    const cl = lrC[i];
    const up = cl >= o;

    let volZ: number | null = null;
    if (hasVolume && i >= VOL_LOOKBACK) {
      let sum = 0, cnt = 0;
      for (let j = i - VOL_LOOKBACK; j < i; j++) {
        if (isFinite(vols[j])) { sum += vols[j]; cnt++; }
      }
      if (cnt >= Math.floor(VOL_LOOKBACK / 2) && isFinite(vols[i])) {
        const mean = sum / cnt;
        let sq = 0;
        for (let j = i - VOL_LOOKBACK; j < i; j++) {
          if (isFinite(vols[j])) sq += (vols[j] - mean) ** 2;
        }
        const sd = Math.sqrt(sq / cnt);
        // A flat-volume window has no meaningful spread; report null instead of
        // dividing by ~0 and calling every bar an outlier.
        volZ = sd > 1e-9 ? (vols[i] - mean) / sd : null;
      }
    }

    out[i] = {
      time: candles[i].time as number,
      lrOpen: o,
      lrClose: cl,
      lrBodyHigh: Math.max(o, cl),
      lrBodyLow: Math.min(o, cl),
      up,
      volZ,
      volContradiction: volZ !== null && volZ <= CONTRADICTION_Z,
    };
  }
  return out;
}
